// How the delivered card list becomes the list on screen: the trader's sort, and the display limit.
//
// Pure functions over the delivered array. Nothing here reads a quote, a setting store or a
// component — so a tick cannot reach it, which is half of why a tick can never re-order the column
// (A3 / guarantee G8). The other half is the caller: it re-runs this ONLY on an explicit sort, never
// on a price update.

import {MavenCard} from './maven-delivery.contract';

/**
 * The trader's sort keys. The delivered order is `PerDay` descending — the run's own ranking.
 *
 * Two keys and no third (owner, vrequest 2026-09-13: "sort by cost per day, and then total cost. I
 * think these two are enough"). A date key existed and was removed on that ruling.
 */
export type MavenSortKey = 'perDay' | 'total';
export type MavenSortDirection = 'asc' | 'desc';

/**
 * The floor under the "show top N" display limit.
 *
 * Maven never offers a single package (guarantee G1), so a column showing one card would state
 * something about the run that is not true. Whatever the setting says, two cards show.
 */
export const MAVEN_MIN_VISIBLE_CARDS = 2;

/**
 * Sort a delivered card list for display. Returns a NEW array; the delivered order is never mutated,
 * because the rank badges are read from the cards themselves and the delivered order has to survive
 * for a reset back to it.
 *
 * `rank` is NOT a sort key and is not derived here. A badge is the RUN's rank and stays on its card
 * through every re-sort (R1) — the row's position and the card's number are different facts.
 *
 * Unpriced figures sort LAST in both directions and are never read as zero (guarantee G4). In
 * practice a package that cannot be priced produces no card at all, so this is a guard, not a path.
 */
export function sortMavenCards(
    cards: MavenCard[],
    key: MavenSortKey,
    direction: MavenSortDirection = 'desc'
): MavenCard[] {

    if (!cards || cards.length === 0) {
        return [];
    }

    const sign = direction === 'asc' ? 1 : -1;

    return cards.slice().sort((a, b) => {

        const left = key === 'perDay' ? a.prices?.perDay : a.prices?.total;
        const right = key === 'perDay' ? b.prices?.perDay : b.prices?.total;

        const leftMissing = left === null || left === undefined;
        const rightMissing = right === null || right === undefined;

        // Missing sorts last whichever way the trader sorted — it is not a value, so it does not
        // take part in the ordering.
        if (leftMissing && rightMissing) {
            return 0;
        }
        if (leftMissing) {
            return 1;
        }
        if (rightMissing) {
            return -1;
        }

        return sign * compareDefined(left, right);
    });
}

/**
 * Apply the ORION "show top N" setting as a DISPLAY limit (R1, ruled 2026-09-10).
 *
 * This is not a cut on what was delivered — the run delivers every day it worked out, and that whole
 * array stays in memory, keeps its subscriptions and keeps its ranks. This only decides how many
 * cards the column draws.
 *
 * At least MAVEN_MIN_VISIBLE_CARDS always show. A limit that is absent, zero or negative means no
 * limit.
 *
 * Nothing here reports how many cards the limit is holding back, and no caller should: a count of
 * hidden cards is the reconciliation handle R-C removes.
 */
export function applyMavenDisplayLimit(cards: MavenCard[], topN: number | null | undefined): MavenCard[] {

    if (!cards || cards.length === 0) {
        return [];
    }

    if (topN === null || topN === undefined || topN <= 0) {
        return cards.slice();
    }

    const limit = Math.max(topN, MAVEN_MIN_VISIBLE_CARDS);

    return cards.slice(0, limit);
}

function compareDefined(a: number, b: number): number {
    if (a === b) {
        return 0;
    }

    return a < b ? -1 : 1;
}
